import { BehaviorSubject, Observable, Subject, Subscription, takeUntil } from 'rxjs';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';

import { Inject, Injectable, OnDestroy } from '@angular/core';

@Injectable({
    providedIn: 'root'
})
export class WebsocketService implements OnDestroy {
    destroy$ = new Subject<void>();

    private wsSubject?: WebSocketSubject<any>;
    private heartbeatSubscription?: Subscription;
    private readonly messageRetryCount = 2;
    private connectionStatus$ = new BehaviorSubject<'init' | 'connected' | 'disconnected' | 'logged'>('init');

    // Store BehaviorSubjects for each topic/subtopic
    private topicObservables: { [key: string]: BehaviorSubject<any> } = {};

    // Track subscription reference counts
    private subscriptionCounts: { [key: string]: number } = {};

    topicsValues: { [key: string]: any } = {};
    topicsSubs: { [key: string]: string[] } = {};

    jwt?: string
    retryTimeout: any

    eventsSubject$ = new Subject<any>();

    constructor(
        @Inject("ENVIRONMENT") private ENV: any
    ) {
        window.addEventListener('beforeunload', this.handleBeforeUnload.bind(this));
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        window.removeEventListener('beforeunload', this.handleBeforeUnload.bind(this));
        this.disconnectWs();
    }


    /**
     * Handle before unload event (page refresh/close)
     */
    private handleBeforeUnload(event: BeforeUnloadEvent): void {
        this.disconnectWs(false, false);
    }

    /**
     * Get connection status as an observable
     */
    getConnectionStatus(): Observable<'init' | 'connected' | 'disconnected' | 'logged'> {
        return this.connectionStatus$.asObservable();
    }

    /**
     * Connect to WebSocket with JWT authentication
     */
    connectWs(jwt: string) {
        // Disconnect if already connected
        if (this.wsSubject) {
            this.disconnectWs();
        }

        this.jwt = jwt;
        var wsConfig = {
            url: this.ENV.wsUrl,
            openObserver: {
                next: () => {
                    this.connectionStatus$.next('connected');
                    this.sendWsMessage({
                        token: jwt
                    });

                    // Resubscribe to existing topics after reconnection
                    // This will only happen if we're reconnecting after an error
                    if (Object.keys(this.subscriptionCounts).length > 0) {
                        this.resubscribeToTopics();
                    }
                }
            },
            closeObserver: {
                next: (c: CloseEvent) => {
                    this.connectionStatus$.next('disconnected');
                    if (c.code !== 1005) {
                        this.disconnectWs(true, true);
                    } else {
                        this.disconnectWs()
                    }
                }
            },
            serializer: (msg: any) => {
                try {
                    return JSON.stringify(msg);
                } catch (error) {
                    this.handleWsError();
                    return '';
                }
            },
            deserializer: (event: MessageEvent) => {
                try {
                    return JSON.parse(event.data);
                } catch (error) {
                    this.handleWsError();
                    return null;
                }
            }
        };

        this.wsSubject = webSocket(wsConfig);

        this.wsSubject.pipe(
            takeUntil(this.destroy$)
        ).subscribe({
            next: this.handleWsMessage.bind(this),
            error: () => this.handleWsError(),
            complete: () => this.connectionStatus$.next('disconnected')
        });
    }

    /**
     * Resubscribe to all topics after reconnection
     */
    private resubscribeToTopics() {
        Object.keys(this.subscriptionCounts).forEach(topicKey => {
            // Only resubscribe if there are active subscribers
            if (this.subscriptionCounts[topicKey] > 0) {
                // Parse the topic key to get topic and subtopic
                var [topic, subtopic] = this.parseTopicKey(topicKey);

                this.sendWsMessage({
                    type: "TOPIC_SUBSCRIBE",
                    topic: topic,
                    subtopic: subtopic
                });
            }
        });
    }

    /**
     * Send message to WebSocket with retry logic
     */
    private sendWsMessage(message: any, retryCount = 0) {
        if (!this.wsSubject) return;

        try {
            this.wsSubject.next(message);
        } catch (error) {
            if (retryCount < this.messageRetryCount) {
                setTimeout(() => {
                    this.sendWsMessage(message, retryCount + 1);
                }, 1000 * (retryCount + 1));
            } else {
                this.handleWsError();
            }
        }
    }

    /**
     * Handle incoming WebSocket messages
     */
    private handleWsMessage(msg: any) {
        if (!msg) return;

        switch (msg.type) {
            case "PING":
                this.sendWsMessage({
                    type: "PING_REPLY",
                });
                break;

            case "LOGGED_IN":
                this.connectionStatus$.next('logged');
                break;

            case "TOPIC_SUBSCRIBED":
                var topic = msg.topic;
                var subtopic = msg.subtopic;

                if (!this.topicsSubs[topic]) {
                    this.topicsSubs[topic] = [];
                }
                if (subtopic && !this.topicsSubs[topic].includes(subtopic)) {
                    this.topicsSubs[topic].push(subtopic);
                }

                var path = [topic];
                if (subtopic) {
                    path.push(subtopic);
                }
                this.setPathKey(this.topicsValues, [...path], undefined);

                // Create BehaviorSubject if it doesn't exist
                var topicKey = this.getTopicKey(topic, subtopic);
                if (!this.topicObservables[topicKey]) {
                    this.topicObservables[topicKey] = new BehaviorSubject<any>(undefined);
                }
                break;

            case "TOPIC_UNSUBSCRIBED":
                var unsubTopic = msg.topic;
                var unsubSubtopic = msg.subtopic;

                if (unsubSubtopic) {
                    if (this.topicsSubs[unsubTopic]) {
                        this.topicsSubs[unsubTopic] = this.topicsSubs[unsubTopic].filter((s) => s != unsubSubtopic);
                    }
                    if (this.topicsSubs[unsubTopic].length == 0) {
                        delete this.topicsSubs[unsubTopic];
                    }
                } else {
                    delete this.topicsSubs[unsubTopic];
                }
                break;

            case "TOPIC_DATA":
                var dataTopic = msg.topic;
                var dataSubtopic = msg.subtopic;
                var dataPath = [dataTopic];
                if (dataSubtopic) {
                    dataPath.push(dataSubtopic);
                }

                // Update internal state
                this.setPathKey(this.topicsValues, [...dataPath], msg.data);

                // Emit new value to subscribers
                var dataTopicKey = this.getTopicKey(dataTopic, dataSubtopic);
                if (this.topicObservables[dataTopicKey]) {
                    if ('fromCache' in msg && msg.fromCache) {
                        msg.data = {
                            ...msg.data,
                            fromCache: true
                        }
                    }
                    this.topicObservables[dataTopicKey].next(msg.data);
                }
                break;

            case "REPORT_NOTIFICATION":
                var dataTopic = msg.userReport;
                this.eventsSubject$.next({
                    type: "REPORT_NOTIFICATION",
                    data: dataTopic
                });
                break;

            case "REBALANCING_STARTED":
                this.eventsSubject$.next({
                    type: "REBALANCING_STARTED"
                });
                break;

            case "REBALANCING_CALCULATED":
                this.eventsSubject$.next({
                    type: "REBALANCING_CALCULATED"
                });
                break;

            case "PREDICTIONS_STARTED":
                this.eventsSubject$.next({
                    type: "PREDICTIONS_STARTED"
                });
                break;

            case "PREDICTIONS_CALCULATED":
                this.eventsSubject$.next({
                    type: "PREDICTIONS_CALCULATED"
                });
                break;

            case "PREDICTIONS_FAILED":
                this.eventsSubject$.next({
                    type: "PREDICTIONS_FAILED"
                });
                break;

            default:
                console.log("WS: Unknown message type", msg);
                break;
        }
    }

    /**
     * Handle WebSocket errors
     */
    private handleWsError() {
        this.disconnectWs(true, true);
    }

    /**
     * Disconnect WebSocket and clean up
     */
    disconnectWs(keepSubs: boolean = false, reconnect: boolean = false) {
        if (this.wsSubject) {
            this.wsSubject.complete();
            this.wsSubject = undefined;
        }

        this.connectionStatus$.next('disconnected');
        if (!keepSubs) {
            this.topicsSubs = {};
            this.subscriptionCounts = {};
            this.topicObservables = {};
            this.topicsValues = {};
        }

        if (reconnect) {
            clearTimeout(this.retryTimeout);
            this.retryTimeout = setTimeout(() => {
                if (this.jwt && this.connectionStatus$.getValue() == "disconnected") {
                    this.connectWs(this.jwt);
                }
            }, 5000);
        } else {
            this.jwt = undefined;
        }
    }

    /**
     * Subscribe to a topic/subtopic with reference counting
     */
    subscribe(topic: string, subtopic?: string): Observable<any> {
        var topicKey = this.getTopicKey(topic, subtopic);

        // Create BehaviorSubject if it doesn't exist
        if (!this.topicObservables[topicKey]) {
            this.topicObservables[topicKey] = new BehaviorSubject<any>(undefined);
        }

        // Increment subscription count
        this.subscriptionCounts[topicKey] = (this.subscriptionCounts[topicKey] || 0) + 1;

        // Only send subscription request if this is the first subscriber
        if (this.subscriptionCounts[topicKey] === 1) {
            this.sendWsMessage({
                type: "TOPIC_SUBSCRIBE",
                topic: topic,
                subtopic: subtopic
            });
        }

        // Return observable that will emit topic updates
        // Use a finalize operator to handle unsubscription
        return new Observable<any>(observer => {
            var subscription = this.topicObservables[topicKey].subscribe(observer);

            return () => {
                subscription.unsubscribe();
                this.decrementSubscription(topic, subtopic);
            };
        });
    }

    /**
     * Decrement subscription count and unsubscribe if no more subscribers
     */
    private decrementSubscription(topic: string, subtopic?: string) {
        var topicKey = this.getTopicKey(topic, subtopic);

        if (this.subscriptionCounts[topicKey]) {
            this.subscriptionCounts[topicKey]--;

            // If no more subscribers, unsubscribe from the topic
            if (this.subscriptionCounts[topicKey] === 0) {
                this.sendWsMessage({
                    type: "TOPIC_UNSUBSCRIBE",
                    topic: topic,
                    subtopic: subtopic
                });

                // Clean up the BehaviorSubject when there are no more subscribers
                if (this.topicObservables[topicKey]) {
                    this.topicObservables[topicKey].complete();
                    delete this.topicObservables[topicKey];
                }

                // Remove from subscription counts
                delete this.subscriptionCounts[topicKey];
            }
        }
    }

    /**
     * Manually unsubscribe from a topic/subtopic
     * This is mainly for cleanup purposes
     */
    unsubscribe(topic: string, subtopic?: string) {
        var topicKey = this.getTopicKey(topic, subtopic);

        // Force unsubscription regardless of count
        if (this.subscriptionCounts[topicKey]) {
            this.subscriptionCounts[topicKey] = 0;

            this.sendWsMessage({
                type: "TOPIC_UNSUBSCRIBE",
                topic: topic,
                subtopic: subtopic
            });

            // Complete and remove the BehaviorSubject
            if (this.topicObservables[topicKey]) {
                this.topicObservables[topicKey].complete();
                delete this.topicObservables[topicKey];
            }

            // Remove from subscription counts
            delete this.subscriptionCounts[topicKey];
        }
    }

    /**
     * Get current value for a topic/subtopic
     */
    getTopicValue(topic: string, subtopic?: string): any {
        if (subtopic) {
            return this.topicsValues[topic]?.[subtopic];
        }
        return this.topicsValues[topic];
    }

    /**
     * Create a unique key for topic/subtopic
     */
    private getTopicKey(topic: string, subtopic?: string): string {
        return subtopic && !subtopic.startsWith("*.") ? `${topic}:${subtopic}` : topic;
    }

    /**
     * Parse a topic key into topic and subtopic
     */
    private parseTopicKey(topicKey: string): [string, string | undefined] {
        var parts = topicKey.split(':');
        return [parts[0], parts.length > 1 ? parts[1] : undefined];
    }

    /**
     * Set a value at a specific path in an object
     */
    setPathKey(obj: any, path: string[], value: any) {
        if (path.length === 0) {
            return value;
        }

        var key = path.shift();
        if (!key) return obj;

        if (path.length === 0) {
            obj[key] = value;
        } else {
            if (!obj[key]) {
                obj[key] = {};
            }
            obj[key] = this.setPathKey(obj[key], path, value);
        }

        return obj;
    }
}
